import { ClientRect, useDraggable } from '@dnd-kit/core'
import { DateTime, Interval } from 'luxon'
import { MutableRefObject } from 'react'
import { Link } from 'react-router-dom'
import { Location } from '../../shared/types'
import { DashboardEntry } from '../api/dashboard'
import { useOverspeedFlaggedMap } from '../api/overspeed'
import { ClientFormatter } from '../components/client-formatter'
import { CurrencyFormatter } from '../components/currency-formatter'
import { SOURCE_COLOR } from '../components/source-formatter'
import { Tooltip, TooltipWrap } from '../components/tooltip'
import { mergeRefs } from '../merge-refs'
import { ACTION_COLOR, BASE_BORDER, BASE_COLOR } from './consts'
import { HorizontalDragOverlay, VerticalDragOverlay } from './drag-overlays'
import { EntryTooltip } from './entry-tooltip'

export function Entry({
	interval,
	seconds,
	entry,
	dateFrom,
	dateTo,
	top,
	containerRect,
	locations,
}: {
	interval: Interval<true>
	seconds: number
	entry: DashboardEntry
	dateFrom: DateTime
	dateTo: DateTime
	top: number
	containerRect: MutableRefObject<ClientRect | null>
	locations: Record<string, Location>
}) {
	const canDragLeft = !entry.recurring && entry.dateFrom >= interval.start
	const canDragRight = !entry.recurring && entry.dateTo <= interval.end
	const ld = useDraggable({
		id: `l-${entry.id}`,
		disabled: !canDragLeft,
		data: {
			type: 'left',
			entry,
		},
	})
	const rd = useDraggable({
		id: `r-${entry.id}`,
		disabled: !canDragRight,
		data: {
			type: 'right',
			entry,
		},
	})
	const cd = useDraggable({
		id: `c-${entry.id}`,
		data: {
			type: 'center',
			entry,
		},
	})
	const overspeedMap = useOverspeedFlaggedMap()
	const flagged = overspeedMap[String(entry.id)]
	const left = (dateFrom.diff(interval.start, 'seconds').seconds / seconds) * 100 + '%'
	const width = (dateTo.diff(dateFrom, 'seconds').seconds / seconds) * 100 + '%'

	const getRoundedClassName = () => {
		if (ld.isDragging) return 'rounded-r'
		if (rd.isDragging) return 'rounded-l'
		if (entry.recurring) return 'rounded-l'
		return 'rounded'
	}
	const colorKey = entry.status
	const className = `focus:outline-1 absolute border-box bg-gradient-to-b h-5 flex items-center justify-between ${BASE_COLOR[colorKey]} border ${getRoundedClassName()} ${BASE_BORDER[colorKey]} ${cd.isDragging ? 'opacity-50' : ''}`
	const locationBar = `linear-gradient(to right, ${locations[entry.location_from]?.color ?? 'transparent'} 25%, ${locations[entry.location_to ?? '']?.color ?? 'transparent'} 75%)`
	// @ts-expect-error custom property source is not typed
	const sourceBar = SOURCE_COLOR[entry.source ?? 'default'] ?? ''

	return (
		<>
			<div
				ref={mergeRefs([ld.setNodeRef, rd.setNodeRef, cd.setNodeRef])}
				tabIndex={0}
				className={className}
				style={{ top, left, width }}
			>
				{ld.transform ? (
					<HorizontalDragOverlay
						width={Math.abs(-1 * ld.transform.x)}
						left={Math.min(ld.transform.x, 0)}
						entry={entry}
						seconds={seconds}
						type="left"
						containerRect={containerRect}
					/>
				) : null}
				{canDragLeft && (
					<div
						className={`cursor-ew-resize h-full w-[6px] flex-initial ${ACTION_COLOR[colorKey]}`}
						ref={ld.setActivatorNodeRef}
						{...ld.listeners}
						{...ld.attributes}
					/>
				)}
				<div className="flex flex-1 items-center min-w-0 relative leading-5">
					<div
						className="absolute left-0 right-0 bottom-0 h-[2px]"
						style={{ background: locationBar }}
					/>
					<div className={`absolute left-0 right-0 top-0 h-[2px] ${sourceBar}`} />
					<div
						className="text-sm flex-auto flex justify-between gap-1 h-full cursor-grab pl-[2px] text-nowrap text-xs overflow-hidden"
						ref={cd.setActivatorNodeRef}
						{...cd.listeners}
						{...cd.attributes}
					>
						<span className="shrink-[1] overflow-hidden">
							<ClientFormatter id={entry.client_id} />
						</span>
						<span className="shrink-[2] overflow-hidden">
							<CurrencyFormatter value={entry.price} fractions={false} />
						</span>
					</div>
					{flagged ? (
						<Tooltip
							content={`Překročení rychlosti: ${flagged.incident_count}× · max ${flagged.max_speed} km/h · ${flagged.is_short_term ? 'krátkodobý (pokuta)' : 'dlouhodobý (upozornit)'}`}
							delay={150}
						>
							<TooltipWrap>
								<span className="px-1 text-[10px] font-bold leading-5 text-rose-700">
									⚠{flagged.max_speed}
								</span>
							</TooltipWrap>
						</Tooltip>
					) : null}
					<Tooltip content={<EntryTooltip entry={entry} />} delay={250}>
						<TooltipWrap>
							<Link to={`/svc/reservation/${entry.id}`} className="px-2">
								i
							</Link>
						</TooltipWrap>
					</Tooltip>
				</div>
				{canDragRight && (
					<div
						className={`cursor-ew-resize h-full w-[6px] flex-initial ${ACTION_COLOR[colorKey]}`}
						ref={rd.setActivatorNodeRef}
						{...rd.listeners}
						{...rd.attributes}
					/>
				)}
				{rd.transform ? (
					<HorizontalDragOverlay
						width={Math.abs(-1 * rd.transform.x)}
						right={Math.min(-1 * rd.transform.x, 0)}
						entry={entry}
						seconds={seconds}
						type="right"
						containerRect={containerRect}
					/>
				) : null}
			</div>
			{cd.isDragging ? (
				<VerticalDragOverlay
					entry={entry}
					top={top}
					left={left}
					width={width}
					seconds={seconds}
					transform={cd.transform}
					containerRect={containerRect}
				/>
			) : null}
		</>
	)
}

export function RecurringEntry({
	interval,
	seconds,
	entry,
	dateFrom,
	top,
	locations,
}: {
	interval: Interval<true>
	seconds: number
	entry: DashboardEntry
	dateFrom: DateTime
	top: number
	locations: Record<string, Location>
}) {
	const left = (dateFrom.diff(interval.start, 'seconds').seconds / seconds) * 100 + '%'
	const width = (interval.end.diff(dateFrom, 'seconds').seconds / seconds) * 100 + '%'

	const colorKey = entry.status
	const className = `absolute border-box bg-gradient-to-b h-5 flex items-center justify-between ${BASE_COLOR[colorKey]} border ${BASE_BORDER[colorKey]} rounded-r opacity-50`
	const locationBar = `linear-gradient(to right, ${locations[entry.location_from]?.color ?? 'transparent'} 25%, ${locations[entry.location_to ?? '']?.color ?? 'transparent'} 75%)`
	// @ts-expect-error custom property source is not typed
	const sourceBar = SOURCE_COLOR[entry.source ?? 'default'] ?? ''

	return (
		<div className={className} style={{ top, left, width }}>
			<div className="flex flex-1 items-center min-w-0 relative leading-5">
				<div
					className="absolute left-0 right-0 bottom-0 h-[2px]"
					style={{ background: locationBar }}
				/>
				<div className={`absolute left-0 right-0 top-0 h-[2px] ${sourceBar}`} />
				<div className="text-sm flex-auto flex justify-between gap-1 h-full pl-[2px] text-nowrap text-xs overflow-hidden">
					<span className="shrink-[1] overflow-hidden">
						<ClientFormatter id={entry.client_id} />
					</span>
					<span className="shrink-[2] overflow-hidden">
						<CurrencyFormatter value={entry.price} fractions={false} />
					</span>
				</div>
				<Tooltip content={<EntryTooltip entry={entry} />} delay={250}>
					<TooltipWrap>
						<Link to={`/svc/reservation/${entry.id}`} className="px-2">
							i
						</Link>
					</TooltipWrap>
				</Tooltip>
			</div>
		</div>
	)
}
